import React, { useEffect, useRef } from 'react';

import { Box, styled, Card, CardContent, Grid, Switch } from '@mui/material';

import { Controller, useForm } from 'react-hook-form';
import { GoldStandardCategory } from '~/src/generated/portal/graphql';
import SkuSelectInput, { SkuOption } from '../SkuSelectInput/SkuSelectInput';

export const GOLD_STANDARD_CATEGORY_MAPPING: Record<string, GoldStandardCategory> = {
  purchaseActivationCancellation: GoldStandardCategory.PurchaseActivationCancel,
  resubscription: GoldStandardCategory.Resubscribe,
  bundleBillingHold: GoldStandardCategory.BillingHold,
  gracePeriod: GoldStandardCategory.GracePeriod,
  offerTransitions: GoldStandardCategory.OfferTransition,
  tierDowngrade: GoldStandardCategory.TierDowngrade,
  tierUpgrade: GoldStandardCategory.TierUpgrade,
};

export interface GoldStandardSetCardProps {
  dropdownSkus?: SkuOption[];
  enableAllSets?: boolean;
  onMainToggleChange?: (enabled: boolean) => void;
  onFormValuesChange?: (values: any) => void;
  hasError?: boolean;
  onClearError?: (errorType?: 'sets' | 'transitions') => void;
  hasSkuValue?: boolean;
}
type GoldStandardFormValues = {
  purchaseActivationCancellation: boolean;
  resubscription: boolean;
  bundleBillingHold: boolean;
  gracePeriod: boolean;
  offerTransitions: {
    offerTransition: boolean;
    transition1: string;
    transition2: string;
  };
  tierDowngrade: {
    tierDowngrade: boolean;
    transition1: string;
    transition2: string;
  };
  tierUpgrade: {
    tierUpgrade: boolean;
    transition1: string;
    transition2: string;
  };
  skuTransitions: string;
  skuTransitions2: string;
};

export const defaultGoldStandardFormValues: GoldStandardFormValues = {
  purchaseActivationCancellation: false,
  resubscription: false,
  bundleBillingHold: false,
  gracePeriod: false,
  offerTransitions: {
    offerTransition: false,
    transition1: '',
    transition2: '',
  },
  tierDowngrade: {
    tierDowngrade: false,
    transition1: '',
    transition2: '',
  },
  tierUpgrade: {
    tierUpgrade: false,
    transition1: '',
    transition2: '',
  },
  skuTransitions: '',
  skuTransitions2: '',
};
const GoldStandardProperty = styled(Box)(() => ({
  padding: '16px 0px 16px 16px',
  fontSize: '16px',
  display: 'flex',
}));

const GoldStandardSetCard = ({
  dropdownSkus,
  enableAllSets,
  onMainToggleChange,
  onFormValuesChange,
  hasError,
  onClearError,
  hasSkuValue,
}: GoldStandardSetCardProps) => {
  const methods = useForm<GoldStandardFormValues>({
    defaultValues: defaultGoldStandardFormValues,
  });
  const { control, watch, setValue, setError, clearErrors, reset } = methods;
  const isUpdatingFromParent = useRef(false);

  const watchTransition1 = watch('offerTransitions.transition1');
  const watchTransition2 = watch('offerTransitions.transition2');
  const watchTierDowngradeTransition1 = watch('tierDowngrade.transition1');
  const watchTierDowngradeTransition2 = watch('tierDowngrade.transition2');
  const watchTierUpgradeTransition1 = watch('tierUpgrade.transition1');
  const watchTierUpgradeTransition2 = watch('tierUpgrade.transition2');

  useEffect(() => {
    if (watchTransition1 || watchTransition2) {
      setValue('offerTransitions.offerTransition', true);
    }
  }, [watchTransition1, watchTransition2, setValue]);

  useEffect(() => {
    if (watchTierDowngradeTransition1 || watchTierDowngradeTransition2) {
      setValue('tierDowngrade.tierDowngrade', true);
    }
  }, [watchTierDowngradeTransition1, watchTierDowngradeTransition2, setValue]);

  useEffect(() => {
    if (watchTierUpgradeTransition1 || watchTierUpgradeTransition2) {
      setValue('tierUpgrade.tierUpgrade', true);
    }
  }, [watchTierUpgradeTransition1, watchTierUpgradeTransition2, setValue]);

  const watchPurchaseActivation = watch('purchaseActivationCancellation');
  const watchResubscription = watch('resubscription');
  const watchBundleBilling = watch('bundleBillingHold');
  const watchGracePeriod = watch('gracePeriod');
  const watchOfferTransition = watch('offerTransitions.offerTransition');
  const watchTierDowngrade = watch('tierDowngrade.tierDowngrade');
  const watchTierUpgrade = watch('tierUpgrade.tierUpgrade');

  useEffect(() => {
    if (hasError) {
      if (watchOfferTransition && !watchTransition1) {
        setError('offerTransitions.transition1', { type: 'manual', message: 'Required' });
      } else {
        clearErrors('offerTransitions.transition1');
      }
      if (watchTierDowngrade && !watchTierDowngradeTransition1) {
        setError('tierDowngrade.transition1', { type: 'manual', message: 'Required' });
      } else {
        clearErrors('tierDowngrade.transition1');
      }
      if (watchTierUpgrade && !watchTierUpgradeTransition1) {
        setError('tierUpgrade.transition1', { type: 'manual', message: 'Required' });
      } else {
        clearErrors('tierUpgrade.transition1');
      }
    } else {
      clearErrors('offerTransitions.transition1');
      clearErrors('tierDowngrade.transition1');
      clearErrors('tierUpgrade.transition1');
    }
  }, [
    hasError,
    watchOfferTransition,
    watchTierDowngrade,
    watchTierUpgrade,
    watchTransition1,
    watchTierDowngradeTransition1,
    watchTierUpgradeTransition1,
    setError,
    clearErrors,
  ]);

  useEffect(() => {
    if (hasError && onClearError) {
      const hasActiveSets = [
        watchPurchaseActivation,
        watchResubscription,
        watchBundleBilling,
        watchGracePeriod,
        watchOfferTransition,
        watchTierDowngrade,
        watchTierUpgrade,
      ].some(Boolean);

      const transitionValidation = [
        { enabled: watchOfferTransition, value: watchTransition1 },
        { enabled: watchTierDowngrade, value: watchTierDowngradeTransition1 },
        { enabled: watchTierUpgrade, value: watchTierUpgradeTransition1 },
      ].every(({ enabled, value }) => !enabled || value);

      // Clear "At least one set needs to be active" error when any toggle is on
      if (hasActiveSets) {
        onClearError('sets');
      }

      // Clear transition errors when validation passes
      if (hasActiveSets && transitionValidation) {
        onClearError('transitions');
      }
    }
  }, [
    hasError,
    watchPurchaseActivation,
    watchResubscription,
    watchBundleBilling,
    watchGracePeriod,
    watchOfferTransition,
    watchTierDowngrade,
    watchTierUpgrade,
    watchTransition1,
    watchTierDowngradeTransition1,
    watchTierUpgradeTransition1,
    onClearError,
  ]);

  // Reset form when SKU value is removed
  useEffect(() => {
    if (!hasSkuValue) {
      reset(defaultGoldStandardFormValues);
      onClearError?.();
      onMainToggleChange?.(false);
    }
  }, [hasSkuValue, reset, onClearError, onMainToggleChange]);

  useEffect(() => {
    if (enableAllSets === true) {
      isUpdatingFromParent.current = true;
      setValue('purchaseActivationCancellation', true);
      setValue('resubscription', true);
      setValue('bundleBillingHold', true);
      setValue('gracePeriod', true);
      setValue('offerTransitions.offerTransition', true);
      setValue('tierDowngrade.tierDowngrade', true);
      setValue('tierUpgrade.tierUpgrade', true);
      setTimeout(() => {
        isUpdatingFromParent.current = false;
      }, 0);
    } else if (enableAllSets === false && !isUpdatingFromParent.current) {
      const allEnabled = [
        watchPurchaseActivation,
        watchResubscription,
        watchBundleBilling,
        watchGracePeriod,
        watchOfferTransition,
        watchTierDowngrade,
        watchTierUpgrade,
      ].every(Boolean);

      if (allEnabled) {
        isUpdatingFromParent.current = true;
        setValue('purchaseActivationCancellation', false);
        setValue('resubscription', false);
        setValue('bundleBillingHold', false);
        setValue('gracePeriod', false);
        setValue('offerTransitions.offerTransition', false);
        setValue('tierDowngrade.tierDowngrade', false);
        setValue('tierUpgrade.tierUpgrade', false);
        setTimeout(() => {
          isUpdatingFromParent.current = false;
        }, 0);
      }
    }
  }, [enableAllSets, setValue]);

  useEffect(() => {
    if (isUpdatingFromParent.current) return;

    const allToggles = [
      watchPurchaseActivation,
      watchResubscription,
      watchBundleBilling,
      watchGracePeriod,
      watchOfferTransition,
      watchTierDowngrade,
      watchTierUpgrade,
    ];

    const anyOff = allToggles.some((toggle) => !toggle);

    if (enableAllSets && anyOff) {
      onMainToggleChange?.(false);
    }
  }, [
    watchPurchaseActivation,
    watchResubscription,
    watchBundleBilling,
    watchGracePeriod,
    watchOfferTransition,
    watchTierDowngrade,
    watchTierUpgrade,
    enableAllSets,
    onMainToggleChange,
  ]);

  useEffect(() => {
    if (onFormValuesChange) {
      const values = {
        purchaseActivationCancellation: watchPurchaseActivation,
        resubscription: watchResubscription,
        bundleBillingHold: watchBundleBilling,
        gracePeriod: watchGracePeriod,
        offerTransitions: {
          offerTransition: watchOfferTransition,
          transition1: watchTransition1,
          transition2: watchTransition2,
        },
        tierDowngrade: {
          tierDowngrade: watchTierDowngrade,
          transition1: watchTierDowngradeTransition1,
          transition2: watchTierDowngradeTransition2,
        },
        tierUpgrade: {
          tierUpgrade: watchTierUpgrade,
          transition1: watchTierUpgradeTransition1,
          transition2: watchTierUpgradeTransition2,
        },
      };
      onFormValuesChange(values);
    }
  }, [
    watchPurchaseActivation,
    watchResubscription,
    watchBundleBilling,
    watchGracePeriod,
    watchOfferTransition,
    watchTierDowngrade,
    watchTierUpgrade,
    watchTransition1,
    watchTransition2,
    watchTierDowngradeTransition1,
    watchTierDowngradeTransition2,
    watchTierUpgradeTransition1,
    watchTierUpgradeTransition2,
  ]);
  return (
    <Card style={{ width: '100%', marginTop: '16px' }}>
      <CardContent sx={{ padding: '0 !important' }}>
        <Box>
          <Grid
            container
            spacing={2}
            height={'56px'}
            sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
          >
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty sx={{ fontWeight: '700', fontSize: '14px' }}>
                Gold Standard Set
              </GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 0 }}>
              <GoldStandardProperty sx={{ fontWeight: '700', fontSize: '14px' }}></GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty sx={{ fontWeight: '700', fontSize: '14px', marginLeft: '56px' }}>
                SKU Transitions
              </GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty sx={{ fontWeight: '700', fontSize: '14px' }}></GoldStandardProperty>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            height={'56px'}
            sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
          >
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty>Purchase, Activation & Cancellation</GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 1 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="purchaseActivationCancellation"
                render={({ field }) => <Switch {...field} checked={field.value || false} />}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            height={'56px'}
            sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
          >
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty>Resubscription</GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 1 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="resubscription"
                render={({ field }) => <Switch {...field} checked={field.value || false} />}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <GoldStandardProperty></GoldStandardProperty>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            height={'56px'}
            sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
          >
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty>Bundle Billing Hold</GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 1 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="bundleBillingHold"
                render={({ field }) => <Switch {...field} checked={field.value || false} />}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <GoldStandardProperty></GoldStandardProperty>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            height={'56px'}
            sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
          >
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty>Grace Period</GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 1 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="gracePeriod"
                render={({ field }) => <Switch {...field} checked={field.value || false} />}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <GoldStandardProperty></GoldStandardProperty>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            height={'56px'}
            sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
          >
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty>Offer Transitions</GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 1.5 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="offerTransitions.offerTransition"
                render={({ field }) => <Switch {...field} checked={field.value || false} />}
              />
            </Grid>
            <Grid
              size={{ xs: 3 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '-16px',
                marginRight: '16px',
                alignSelf: 'center',
              }}
            >
              <SkuSelectInput
                control={control}
                name="offerTransitions.transition1"
                options={dropdownSkus ?? []}
                disabled={!watchOfferTransition}
                freeSolo
                hideLabel
                hideErrorMsg
              />
            </Grid>
            <Grid
              size={{ xs: 3 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '16px',
                marginRight: '-16px',
                alignSelf: 'center',
              }}
            >
              <SkuSelectInput
                control={control}
                name="offerTransitions.transition2"
                options={dropdownSkus ?? []}
                disabled={!watchOfferTransition}
                freeSolo
                hideLabel
                hideErrorMsg
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={0}
            height={'56px'}
            sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
          >
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty>Tier Downgrade</GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 1.5 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="tierDowngrade.tierDowngrade"
                render={({ field }) => <Switch {...field} checked={field.value || false} />}
              />
            </Grid>
            <Grid
              size={{ xs: 3 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '-16px',
                marginRight: '16px',
                alignSelf: 'center',
              }}
            >
              <SkuSelectInput
                control={control}
                name="tierDowngrade.transition1"
                options={dropdownSkus ?? []}
                disabled={!watchTierDowngrade}
                freeSolo
                hideLabel
                hideErrorMsg
              />
            </Grid>
            <Grid
              size={{ xs: 3 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '16px',
                marginRight: '-16px',
                alignSelf: 'center',
              }}
            >
              <SkuSelectInput
                control={control}
                name="tierDowngrade.transition2"
                options={dropdownSkus ?? []}
                disabled={!watchTierDowngrade}
                freeSolo
                hideLabel
                hideErrorMsg
              />
            </Grid>
          </Grid>
          <Grid container spacing={0} height={'56px'}>
            <Grid size={{ xs: 4 }}>
              <GoldStandardProperty>Tier Upgrade</GoldStandardProperty>
            </Grid>
            <Grid size={{ xs: 1.5 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="tierUpgrade.tierUpgrade"
                render={({ field }) => <Switch {...field} checked={field.value || false} />}
              />
            </Grid>
            <Grid
              size={{ xs: 3 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '-16px',
                marginRight: '16px',
                alignSelf: 'center',
              }}
            >
              <SkuSelectInput
                control={control}
                name="tierUpgrade.transition1"
                options={dropdownSkus ?? []}
                disabled={!watchTierUpgrade}
                freeSolo
                hideLabel
                hideErrorMsg
              />
            </Grid>
            <Grid
              size={{ xs: 3 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '16px',
                marginRight: '-16px',
                alignSelf: 'center',
              }}
            >
              <SkuSelectInput
                control={control}
                name="tierUpgrade.transition2"
                options={dropdownSkus ?? []}
                disabled={!watchTierUpgrade}
                freeSolo
                hideLabel
                hideErrorMsg
              />
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GoldStandardSetCard;
